"use client";


import { ComponentProps, FormEventHandler, ReactNode, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { InputWrap } from '../InputWrap/InputWrap';
// import { Box } from '@fastlooptech/layout-components';
import { useInputStyle } from '../_common';
import Button from '@/components/Button';
import { Box } from '@/layouts/LayoutComponents';
import { InputsNS } from '@/types/inputs';
import { Icons } from '@/components/Icons';

export type InputTextProps = Omit<ComponentProps<typeof InputWrap>, 'children' | 'htmlFor'> & {
  'data-test-id'?: string;
  /**
   * The id of the input.
   * @type string
   * @required
   */
  id: string;
  /**
   * The aria-label of the input.
   * @type string
   * @optional
   */
  'aria-label'?: string;
  /**
   * The placeholder of the input.
   * @type string
   * @required
   */
  placeholder?: string;
  /**
   * The default value of the input.
   * @type string
   * @optional
   */
  defaultValue?: string;
  /**
   * The name of the input.
   * @type string
   * @required
   */
  name: string;
  /**
   * The boolean that determines if the input is disabled.
   * @type boolean
   * @optional
   * @default false
   */
  disabled?: boolean;
  /**
   * The className of the input.
   * @type string
   * @optional
   */
  className?: string;
  /**
   * The className of the input content.
   * @type string
   * @optional
   */
  classNameContent?: string;
  /**
   * The value of the input.
   * @optional
   */
  value?: string;
  /**
   * The on change function of the input.
   * @optional
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * The on key up function of the input.
   * @optional
   */
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * The on key down function of the input.
   * @optional
   */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * The on blur function of the input.
   * @optional
   */
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * The on focus function of the input.
   * @optional
   */
  onFocus?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Optional icon element.
   * @type ReactNode;
   * @optional
   */
  beforeIcon?: ReactNode;
  /**
   * Optional icon button action.
   * @type () => void;
   * @optional
   */
  beforeIconAction?: () => void;
  /**
   * Optional icon element.
   * @type ReactNode;
   * @optional
   */
  afterIcon?: ReactNode;
  /**
   * Optional icon button action.
   * @type () => void;
   * @optional
   */
  afterIconAction?: (
    value: HTMLInputElement['value'] | undefined,
    event: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>
  ) => void;
  /**
   * If the text input should autocomplete or not.
   * @type "on" | "off"
   * @optional
   * @default "off"
   */
  autoComplete?: 'on' | 'off';
  /**
   * optional input action element.
   * @type ReactNode;
   * @optional
   */
  actionChildren?: ReactNode;
  /**
   * If the text input should update the value or not.
   * @type boolean
   * @optional
   * @default false
   */
  updateValue?: boolean;
  /**
   * Optional max length of the textarea.
   * @type number | string;
   * @optional
   */
  maxLength?: number;
  title?: string;
  onInvalid?: FormEventHandler<HTMLInputElement> | undefined;
} & (
    | {
      /**
       * The allowed types of input.
       * @type "number"
       * @required
       */
      type: 'number';
      min?: number;
      max?: number;
    }
    | {
      /**
       * The allowed types of input.
       * @type "text" | "email" | "tel" | "search" | "password"
       * @required
       */
      type: 'text' | 'email' | 'tel' | 'search' | 'password' | 'date' | 'hidden';
      min?: never;
      max?: never;
    }
  );

export function InputText(props: InputTextProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const inputStyle = useInputStyle(
    props.assistive?.filter((assistive: any) => {
      if (props.assistive && props.assistive?.length === 1) return props.assistive;
      return assistive.type !== InputsNS.StatusType.Informative;
    })[0].type
  );

  const enterKeyListener = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        props.afterIconAction && props.afterIconAction(inputRef.current?.value, event);
      }
    },
    [props]
  );

  useEffect(() => {
    if (props.updateValue && inputRef.current && inputRef.current.value !== props.value) {
      inputRef.current.value = props.value || '';
    }
  }, [props.value, props.updateValue]);

  const InputJSX =  <input
      className={classNames(
        'w-full',
        // Padding
        'py-input-padding-y px-input-padding-x',
        // Hide border
        '!border-transparent',
        // Border radius
        'rounded-input',
        // Text color
        'text-input-default-text disabled:text-input-disabled-text autofill:text-input-autofilled-text',
        // Background color
        'bg-transparent',
        // Placeholder color
        'placeholder:text-input-default-placeholder disabled:placeholder:text-input-disabled-placeholder',
        // Text size
        'text-input',
        // Font family
        'font-input placeholder:font-input',
        // Others
        'text-ellipsis disabled:cursor-not-allowed outline-none',
        // inputStyle.focusBorder
      )}
      id={props.id}
      ref={inputRef}
      data-test-id={props['data-test-id']}
      aria-label={props['aria-label']}
      type={props.type}
      min={props.min}
      max={props.max}
      placeholder={props.placeholder}
      defaultValue={props.value || props.defaultValue}
      value={props.value}
      name={props.name}
      onKeyUp={props.onKeyUp}
      onKeyDown={e => {
        props.onKeyDown?.(e);
        enterKeyListener;
      }}
      disabled={props.disabled}
      onChange={props.onChange}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      required={props.required}
      autoComplete={props.autoComplete}
      maxLength={props?.maxLength || 150}
      // onInvalid={props?.onInvalid}
      // pattern='/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>'

    />

  return (
    props?.type === "hidden" ? InputJSX : 
    <InputWrap
      label={props?.label}
      data-test-id={props['data-test-id'] + '-wrap'}
      assistive={props?.assistive}
      className="relative"
      required={props?.required}
      width={props?.width}
      htmlFor={props?.id}
      disabled={props?.disabled}
      actionChildren={props?.actionChildren}
    >
      <Box
        vAlignContent="center"
        aria-disabled={props?.disabled}
        grow
        shrink
        className={classNames(
          // Size
          '!w-input-width !h-input-height',
          // Border color
          '!border !border-input-default-border autofill:!border-input-autofilled-border aria-disabled:!border-input-disabled-border',
          // Border width
          '!border-input !border-b-input-b',
          // Border radius
          '!rounded-input',
          // Text color
          'aria-disabled:!text-input-disabled-text',
          // Background color
          '!bg-input-default-bg aria-disabled:!bg-input-disabled-bg autofill:!bg-input-autofilled-bg',
          // Others
          'transition-shadow aria-disabled:hover:shadow-none aria-disabled:cursor-not-allowed',
          inputStyle.border,
          inputStyle.focusBorder,
          props?.className,
        )}
      >
        {props?.beforeIcon && (
          <Button
            id="text-input-icon"
            data-test-id={props['data-test-id'] + '-button'}
            size="m"
            variant="ghost"
            cssClass={classNames('pointer-events-auto !outline-none', {
              '!cursor-not-allowed': props?.disabled,
              'cursor-pointer': !props?.disabled,
            })}
            action={props?.beforeIconAction}
          >
            {props?.beforeIcon}
          </Button>
        )}
        {InputJSX}
        {props?.afterIcon && (
          <Button
            id="text-input-icon"
            data-test-id={props['data-test-id'] + '-button'}
            size="m"
            variant="ghost"
            cssClass={classNames('pointer-events-auto !outline-none', {
              '!cursor-not-allowed': props?.disabled,
              'cursor-pointer': !props?.disabled,
            })}
            action={event => props?.afterIconAction && props.afterIconAction(inputRef.current?.value, event)}
          >
            {props.afterIcon}
          </Button>
        )}
      </Box>
    </InputWrap>
  );
}
